import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";
import { scrollToSection } from "./Header";

const HERO_BG =
  "https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2000&auto=format&fit=crop";

const BADGES = ["SaaS Platforms", "Asset Leasing", "Retail Trading", "Global Commerce"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex flex-col text-white"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Modern corporate headquarters with warm ambient lighting and greenery"
          data-testid="hero-image"
          className="w-full h-full object-cover scale-110"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/75 via-navy/40 to-navy-deep/85 backdrop-blur-[2px]" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/60 via-navy-deep/20 to-transparent" />

      <div className="relative flex-1 max-w-7xl w-full mx-auto px-6 lg:px-10 flex flex-col justify-center pt-36 lg:pt-40 pb-28">
        <FadeUp delay={0.1}>
          <p
            data-testid="hero-overline"
            className="uppercase tracking-[0.28em] text-xs sm:text-sm font-semibold text-white/70 flex items-center gap-3"
          >
            <span className="inline-block w-10 h-px bg-white/60" />
            From India to the World
          </p>
        </FadeUp>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-bold text-5xl sm:text-6xl lg:text-7xl leading-[1.08] tracking-tight max-w-4xl"
        >
          <MaskedLine delay={0.25}>Driving Value Across</MaskedLine>
          <MaskedLine delay={0.38}>
            <span className="text-[#C9D3F5]">Software, Real Estate</span>
          </MaskedLine>
          <MaskedLine delay={0.51}>&amp; Global Commerce</MaskedLine>
        </h1>

        <FadeUp delay={0.7}>
          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
          >
            Kritajna Ventures LLP is a diversified parent entity focused on scaling modern
            cloud technology, managing commercial real estate infrastructure, and executing
            international trading solutions.
          </p>
        </FadeUp>

        <FadeUp delay={0.85} className="mt-10 flex flex-wrap items-center gap-4">
          <button
            data-testid="explore-portfolio-button"
            onClick={() => scrollToSection("#verticals")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-navy transition-transform duration-300 hover:-translate-y-0.5"
          >
            Explore Our Portfolio
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            href="#about"
            data-testid="hero-about-link"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#about");
            }}
            className="group inline-flex items-center gap-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/20"
          >
            Our Story
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </FadeUp>
      </div>

      <FadeUp delay={1.05} margin="0px" className="absolute bottom-8 inset-x-0">
        <div className="max-w-7xl w-full mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-3" data-testid="hero-badges">
            {BADGES.map((b, i) => (
              <span
                key={b}
                data-testid={`hero-badge-${i}`}
                className="rounded-full bg-white/15 backdrop-blur-md border border-white/30 px-5 py-2.5 text-xs sm:text-sm font-semibold tracking-wide text-white"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </FadeUp>
    </section>
  );
};

export default Hero;
